// TIP-614 — Counterparty Brief extra-purchase money maths.
// THERE ARE NO PRICES IN THIS FILE. Every figure comes from the `pricing`
// block on GET /api/briefs/allowance/, held in store/briefs/briefPricingSlice
// and passed in here. That is deliberate and it is the second attempt:
// This module used to carry `BRIEF_UNIT_PRICE_FILS = 14900`, a member price,
// a VAT rate of 5 and a max quantity of 20, as "mirrors" of Django settings.
// Every one was wrong for at least one live environment — one sells briefs at
// AED 10 with a maximum of 100 — and the drift was undetectable: the backend
// test reads settings, the frontend test asserted the literal, so both suites
// stayed green while the two disagreed. A customer would have seen one price
// in step 1 and Stripe's real one in step 2.
// So the functions below return NULL when they have no pricing block, and the
// UI shows an absent price as absent. A wrong price is worse than no price:
// one is a support ticket, the other is a refund.
// INTEGER FILS END TO END. No float touches a price — a float does not hold
// 134.10, and the VAT line would disagree with Stripe's by a fil on some
// quantities. The only division is the half-up VAT rounding, which is exact.
// A SIBLING of webProfilePricing.js, not an extension of it: that module is
// float-based, and inheriting its arithmetic is what this ticket exists to
// The quote here remains a DISPLAY ARTIFACT. Quantity, tier and price are
// never sent to the backend, which derives all three itself, and Stripe's
// session totals are the record.

/* The quantity range, from the endpoint. */
export const quantityBounds = (pricing = null) => {
  const min = Number(pricing?.min_quantity);
  const max = Number(pricing?.max_quantity);
  const okMin = Number.isInteger(min) && min >= 1 ? min : 1;
  const okMax = Number.isInteger(max) && max >= okMin ? max : okMin;
  return { min: okMin, max: okMax };
};

/* The unit price to compute with, in fils, or null. */
export const unitPriceFils = (isMember, pricing = null) => {
  const candidates = isMember
    ? [pricing?.member_unit_price_fils, pricing?.unit_price_fils]
    : [pricing?.standard_unit_price_fils, pricing?.unit_price_fils];
  const found = candidates.find((v) => Number.isInteger(v) && v > 0);
  return found ?? null;
};

/** The VAT rate the backend says applies, or null. Never assumed to be 5. */
export const vatPercent = (pricing = null) => {
  const rate = Number(pricing?.vat_percent);
  return Number.isFinite(rate) && rate >= 0 ? rate : null;
};

/** Is there enough in this block to draw a price at all? */
export const canPrice = (pricing = null, isMember = false) =>
  unitPriceFils(isMember, pricing) !== null && vatPercent(pricing) !== null;

/* The quote for `quantity` briefs at the buyer's tier. */
export const quote = (quantity, isMember, pricing = null) => {
  const unit = unitPriceFils(isMember, pricing);
  const rate = vatPercent(pricing);
  // No price, no quote. The caller shows the absence rather than a figure we
  if (unit === null || rate === null) return null;

  const netFils = unit * quantity;
  const vatFils = Math.floor((netFils * rate + 50) / 100);
  return {
    unitPriceFils: unit,
    netFils,
    vatFils,
    totalFils: netFils + vatFils,
  };
};

/* What the member discount is worth on this order, in fils. */
export const savingsFils = (quantity, isMember, pricing = null) => {
  if (!isMember) return 0;
  const standard = unitPriceFils(false, pricing);
  const member = unitPriceFils(true, pricing);
  // Only claimable when the endpoint sent BOTH prices and they really differ.
  if (standard === null || member === null || standard <= member) return 0;
  return (standard - member) * quantity;
};

/** The undiscounted figure struck through beside the member price, or null. */
export const listNetFils = (quantity, pricing = null) => {
  const standard = unitPriceFils(false, pricing);
  return standard === null ? null : standard * quantity;
};

/* Fils -> the money string the design draws: "AED 1,048.95". */
export const formatAed = (fils) => {
  const negative = fils < 0;
  const abs = Math.abs(Math.round(fils));
  const aed = Math.floor(abs / 100);
  const cents = abs % 100;
  const grouped = String(aed).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}AED ${grouped}.${String(cents).padStart(2, "0")}`;
};

/* One to twenty in words. The design writes the quantity out in prose — "Three counterparty briefs", "Three briefs added", "You save AED 44.70 on these three"... */
export const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen", "twenty",
];

export const numberWord = (n) => NUMBER_WORDS[n] ?? String(n);

const capitalise = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/** "One brief" / "Three briefs" — the sentence-leading form. */
export const briefsInWords = (quantity) =>
  `${capitalise(numberWord(quantity))} ${quantity === 1 ? "brief" : "briefs"}`;

/** "1 brief" / "3 briefs" — the stepper's own label, digits as drawn. */
export const briefsCount = (quantity) =>
  `${quantity} ${quantity === 1 ? "brief" : "briefs"}`;

/**
 * Kept inside the purchasable range, so the stepper can never offer a
 * quantity the backend would reject with a 400.
 */
export const clampQuantity = (quantity, pricing = null) => {
  const { min, max } = quantityBounds(pricing);
  const n = Math.trunc(Number(quantity));
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};
